import { useEffect, useRef, useState } from 'react';
import { Node, Rect, useReactFlow, useStoreApi, XYPosition } from 'reactflow';
import { getFocusOnState } from '@components/MainStage/focusOn.store';
import { getMultiSelectionState } from '@components/MainStage/multiSelection.store';
import { canvasState } from '@components/MainStage/store';
import { NodesTypes } from '@constants/canvas/general';
import { useProject } from '@context/Project';
import { isElementLocal } from '@utils/drawers';
import { isEditableTextTarget } from '@utils/isEditableTextTarget';

const EDGE_THRESHOLD = 40;
const MAX_PAN_SPEED = 14;
const DRAG_THRESHOLD = 4;

type DragState = {
  moved: boolean;
  pointerId: number;
  startClient: XYPosition;
  lastClient: XYPosition;
  anchorFlow: XYPosition;
  baseIds: string[];
  wasDrawerOpen: boolean;
  wasMultiActive: boolean;
};

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const rectFromPoints = (a: XYPosition, b: XYPosition): Rect => ({
  x: Math.min(a.x, b.x),
  y: Math.min(a.y, b.y),
  width: Math.abs(a.x - b.x),
  height: Math.abs(a.y - b.y),
});

const nodeIntersectsRect = (node: Node, rect: Rect): boolean => {
  const position = node.positionAbsolute ?? node.position;
  const width = node.width ?? 0;
  const height = node.height ?? 0;

  if (!width || !height) return false;

  return (
    position.x < rect.x + rect.width &&
    position.x + width > rect.x &&
    position.y < rect.y + rect.height &&
    position.y + height > rect.y
  );
};

const toggleAll = (base: string[], touched: string[]): string[] => {
  const result = new Set(base);

  touched.forEach((id) => {
    if (result.has(id)) result.delete(id);
    else result.add(id);
  });

  return Array.from(result);
};

const useMarqueeSelection = () => {
  const reactFlow = useReactFlow();
  const store = useStoreApi();
  const { drawer, handleCloseDrawer } = useProject();

  const [isShiftPressed, setIsShiftPressed] = useState(false);
  const [marqueeRect, setMarqueeRect] = useState<Rect | null>(null);

  const reactFlowRef = useRef(reactFlow);
  reactFlowRef.current = reactFlow;
  const drawerRef = useRef(drawer);
  drawerRef.current = drawer;
  const closeDrawerRef = useRef(handleCloseDrawer);
  closeDrawerRef.current = handleCloseDrawer;

  const dragRef = useRef<DragState | null>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const getContainerRect = () =>
      store.getState().domNode?.getBoundingClientRect() ?? null;

    const stopAutoPan = () => {
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };

    const applySelection = () => {
      const drag = dragRef.current;
      const containerRect = getContainerRect();

      if (!drag?.moved || !containerRect) return;

      const relX = clamp(
        drag.lastClient.x - containerRect.left,
        0,
        containerRect.width,
      );
      const relY = clamp(
        drag.lastClient.y - containerRect.top,
        0,
        containerRect.height,
      );

      const currentFlow = reactFlowRef.current.project({ x: relX, y: relY });
      const flowRect = rectFromPoints(drag.anchorFlow, currentFlow);

      const focus = getFocusOnState();

      const touched = reactFlowRef.current
        .getNodes()
        .filter(
          (node) =>
            node.type === NodesTypes.CustomNode &&
            !node.hidden &&
            !isElementLocal(node.id) &&
            (!focus.sourceId || focus.isFocused(node.id)) &&
            nodeIntersectsRect(node, flowRect),
        )
        .map((node) => node.id);

      getMultiSelectionState().setSelection(toggleAll(drag.baseIds, touched));

      const viewport = reactFlowRef.current.getViewport();
      const anchorScreen: XYPosition = {
        x: drag.anchorFlow.x * viewport.zoom + viewport.x,
        y: drag.anchorFlow.y * viewport.zoom + viewport.y,
      };

      setMarqueeRect(rectFromPoints(anchorScreen, { x: relX, y: relY }));
    };

    const autoPanStep = () => {
      const drag = dragRef.current;
      const containerRect = getContainerRect();

      if (!drag?.moved || !containerRect) {
        rafRef.current = null;

        return;
      }

      const speedFor = (distance: number) =>
        clamp(distance / 3, 2, MAX_PAN_SPEED);

      const { x: cursorX, y: cursorY } = drag.lastClient;
      let deltaX = 0;
      let deltaY = 0;

      if (cursorX < containerRect.left + EDGE_THRESHOLD) {
        deltaX = speedFor(containerRect.left + EDGE_THRESHOLD - cursorX);
      } else if (cursorX > containerRect.right - EDGE_THRESHOLD) {
        deltaX = -speedFor(cursorX - (containerRect.right - EDGE_THRESHOLD));
      }

      if (cursorY < containerRect.top + EDGE_THRESHOLD) {
        deltaY = speedFor(containerRect.top + EDGE_THRESHOLD - cursorY);
      } else if (cursorY > containerRect.bottom - EDGE_THRESHOLD) {
        deltaY = -speedFor(cursorY - (containerRect.bottom - EDGE_THRESHOLD));
      }

      if (deltaX !== 0 || deltaY !== 0) {
        store.getState().panBy({ x: deltaX, y: deltaY });
        applySelection();
      }

      rafRef.current = requestAnimationFrame(autoPanStep);
    };

    const endDrag = () => {
      stopAutoPan();
      dragRef.current = null;
      setMarqueeRect(null);
      document.body.style.userSelect = '';
    };

    const cancelDrag = () => {
      const drag = dragRef.current;

      if (drag?.moved) {
        // Restore the selection to its pre-drag state.
        getMultiSelectionState().setSelection(drag.baseIds);
      }

      endDrag();
    };

    const onPointerDown = (event: PointerEvent) => {
      if (event.button !== 0 || !event.shiftKey || dragRef.current) return;

      const target = event.target as HTMLElement | null;

      if (!target?.closest('.react-flow__pane')) return;

      if (
        target.closest('.react-flow__node-customNode') ||
        target.closest('.react-flow__node-newNode') ||
        target.closest('.react-flow__edge') ||
        target.closest('.react-flow__panel') ||
        target.closest('.react-flow__controls')
      ) {
        return;
      }

      const containerRect = getContainerRect();

      if (!containerRect) return;

      const anchorFlow = reactFlowRef.current.project({
        x: event.clientX - containerRect.left,
        y: event.clientY - containerRect.top,
      });

      const multiSelection = getMultiSelectionState();
      const wasMultiActive = multiSelection.selectedIds.length > 0;
      const wasDrawerOpen = Boolean(drawerRef.current);

      let baseIds = multiSelection.selectedIds;

      if (!wasMultiActive && wasDrawerOpen) {
        const { anchorId } = multiSelection;
        baseIds = anchorId && !isElementLocal(anchorId) ? [anchorId] : [];
      }

      dragRef.current = {
        moved: false,
        pointerId: event.pointerId,
        startClient: { x: event.clientX, y: event.clientY },
        lastClient: { x: event.clientX, y: event.clientY },
        anchorFlow,
        baseIds: [...baseIds],
        wasDrawerOpen,
        wasMultiActive,
      };
    };

    const onPointerMove = (event: PointerEvent) => {
      const drag = dragRef.current;

      if (!drag || event.pointerId !== drag.pointerId) return;

      drag.lastClient = { x: event.clientX, y: event.clientY };

      if (!drag.moved) {
        const distance = Math.hypot(
          event.clientX - drag.startClient.x,
          event.clientY - drag.startClient.y,
        );

        if (distance < DRAG_THRESHOLD) return;

        drag.moved = true;
        document.body.style.userSelect = 'none';

        canvasState().unselectActiveNode();
        canvasState().unselectActiveEdge();

        if (rafRef.current === null) {
          rafRef.current = requestAnimationFrame(autoPanStep);
        }
      }

      applySelection();
    };

    const onPointerUp = async (event: PointerEvent) => {
      const drag = dragRef.current;

      if (!drag || event.pointerId !== drag.pointerId) return;

      if (!drag.moved) {
        endDrag();

        return;
      }

      const suppressClick = (clickEvent: MouseEvent) => {
        clickEvent.stopPropagation();
        clickEvent.preventDefault();
      };

      window.addEventListener('click', suppressClick, true);
      setTimeout(
        () => window.removeEventListener('click', suppressClick, true),
        0,
      );

      const committedIds = getMultiSelectionState().selectedIds;

      endDrag();

      if (drag.wasDrawerOpen && !drag.wasMultiActive) {
        try {
          await closeDrawerRef.current();
        } catch {
          getMultiSelectionState().setSelection(drag.baseIds);

          return;
        }

        getMultiSelectionState().setSelection(committedIds);
      }
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (
        event.key === 'Shift' &&
        !isEditableTextTarget(document.activeElement)
      ) {
        setIsShiftPressed(true);
      }

      if (event.key === 'Escape' && dragRef.current) {
        cancelDrag();
      }
    };

    const onKeyUp = (event: KeyboardEvent) => {
      if (event.key === 'Shift') setIsShiftPressed(false);
    };

    const onWindowBlur = () => {
      setIsShiftPressed(false);
      cancelDrag();
    };

    window.addEventListener('pointerdown', onPointerDown, true);
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointercancel', cancelDrag);
    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('keyup', onKeyUp, true);
    window.addEventListener('blur', onWindowBlur);

    return () => {
      stopAutoPan();
      document.body.style.userSelect = '';
      window.removeEventListener('pointerdown', onPointerDown, true);
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointercancel', cancelDrag);
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('keyup', onKeyUp, true);
      window.removeEventListener('blur', onWindowBlur);
    };
  }, [store]);

  return { marqueeRect, isShiftPressed };
};

export default useMarqueeSelection;
